// Mirrors backend/src/utils/passwordPolicy.js. Kept as a plain module so
// forms can compute the checklist state without hitting the server.

export const PASSWORD_POLICY = Object.freeze({
  minLength: 8,
  maxLength: 100,
});

export const PASSWORD_HELPER =
  'Minimum 8 characters, including uppercase, lowercase, number, and symbol.';

export const PASSWORD_WEAK_MSG =
  'Password does not meet security requirements. Please include at least 8 characters, uppercase, lowercase, number, and symbol.';

export const PASSWORD_MISMATCH_MSG =
  'Passwords do not match. Please try again.';

// Rule table drives both the live checklist and the "is-valid" boolean.
export const PASSWORD_RULES = [
  { key: 'length',  label: `At least ${PASSWORD_POLICY.minLength} characters long`, test: (p) => p.length >= PASSWORD_POLICY.minLength },
  { key: 'upper',   label: 'At least one uppercase letter (A–Z)',                   test: (p) => /[A-Z]/.test(p) },
  { key: 'lower',   label: 'At least one lowercase letter (a–z)',                   test: (p) => /[a-z]/.test(p) },
  { key: 'number',  label: 'At least one number (0–9)',                             test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'At least one special character (e.g., ! @ # $ % ^ & *)', test: (p) => /[^A-Za-z0-9\s]/.test(p) },
];

export function passwordChecks(pw) {
  const p = String(pw ?? '');
  return PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(p) }));
}

export function isStrongPassword(pw) {
  return passwordChecks(pw).every((c) => c.passed);
}

// ─── Clinic sign-up variant (client 2026-08-28) ─────────────────────────────
// Softer policy for the clinic onboarding form — 8 chars, grouped rules
// (upper+lower shown as one row, number+symbol as another). Copy + rule
// layout mirror the mock in the mobile-onboarding PDF.
export const CLINIC_PASSWORD_POLICY = Object.freeze({
  minLength: 8,
  maxLength: 100,
});

export const CLINIC_PASSWORD_HELPER =
  'Minimum 8 characters, including uppercase, lowercase, number, and symbol.';

export const CLINIC_PASSWORD_WEAK_MSG = 'Weak Password. Please try again.';

export const CLINIC_PASSWORD_RULES = [
  { key: 'length',   label: `At least ${CLINIC_PASSWORD_POLICY.minLength} characters`,
    test: (p) => p.length >= CLINIC_PASSWORD_POLICY.minLength },
  { key: 'case',     label: 'Uppercase & lowercase',
    test: (p) => /[A-Z]/.test(p) && /[a-z]/.test(p) },
  { key: 'numSym',   label: 'Number & symbol',
    test: (p) => /[0-9]/.test(p) && /[^A-Za-z0-9\s]/.test(p) },
];

export function clinicPasswordChecks(pw) {
  const p = String(pw ?? '');
  return CLINIC_PASSWORD_RULES.map((r) => ({ ...r, passed: r.test(p) }));
}

export function isStrongClinicPassword(pw) {
  return clinicPasswordChecks(pw).every((c) => c.passed);
}
